// Admin → Overview tab: KPIs with trend, activity area, account hygiene, quotas.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import EChart from '../charts/EChart';
import { KpiCard, ChartCard, EmptyState } from './shared';

const ICONS = {
  user: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  analysis: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  activity: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  subscription: 'M13 10V3L4 14h7v7l9-11h-7z'
};

const QuotaBar = ({ label, used, limit }) => {
  const u = Number(used) || 0;
  const l = Number(limit) || 0;
  const pct = l > 0 ? Math.min(100, Math.round((u / l) * 100)) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="text-slate-500">{l > 0 ? `${u} / ${l}` : `${u}`}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${l > 0 ? pct : 0}%` }} />
      </div>
    </div>
  );
};

QuotaBar.propTypes = {
  label: PropTypes.string.isRequired,
  used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

const AdminOverview = ({ view, t, period }) => {
  const { metrics, previous, activityTrends, mfaCoverage, quota, companyInfo } = view;

  const activityOption = useMemo(() => {
    const rows = (activityTrends || []).filter((r) => (r.login || r.create || r.update || r.delete));
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 24, bottom: 48 },
      xAxis: { type: 'category', boundaryGap: false, data: (activityTrends || []).map((r) => r.name) },
      yAxis: { type: 'value' },
      series: ['login', 'create', 'update', 'delete'].map((k) => ({
        name: k,
        type: 'line',
        smooth: true,
        showSymbol: false,
        areaStyle: { opacity: 0.15 },
        data: (activityTrends || []).map((r) => r[k] || 0)
      })),
      _hasData: rows.length > 0
    };
  }, [activityTrends]);

  const hygieneOption = useMemo(() => {
    if (!mfaCoverage) return null;
    const enabled = Number(mfaCoverage.enabled) || 0;
    const disabled = Number(mfaCoverage.disabled) || 0;
    if (enabled + disabled === 0) return null;
    return {
      tooltip: { trigger: 'item' },
      legend: { bottom: 0 },
      series: [{
        type: 'pie',
        radius: ['55%', '75%'],
        label: { show: false },
        data: [
          { name: t('mfaEnabled'), value: enabled },
          { name: t('mfaDisabled'), value: disabled }
        ]
      }]
    };
  }, [mfaCoverage, t]);

  const quotaData = quota || (companyInfo ? {
    usersUsed: metrics.totalUsers,
    usersLimit: companyInfo.userLimit ?? companyInfo.maxUsers ?? null,
    analysesUsed: metrics.totalAnalyses ?? metrics.activeAnalyses,
    analysesLimit: companyInfo.analysisLimit ?? companyInfo.maxAnalyses ?? null
  } : null);

  const subUrgent = (metrics.subscriptionDaysRemaining ?? 999) <= 7;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard title={t('kpiUsers')} value={metrics.totalUsers} previousValue={previous.totalUsers}
          hint={t('kpiUsersHint')} icon={ICONS.user} accent="blue"
          linkText={t('manageUsers')} linkTo="/admin/users" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiAnalyses')} value={metrics.totalAnalyses ?? metrics.activeAnalyses} previousValue={previous.totalAnalyses ?? previous.activeAnalyses}
          hint={t('kpiAnalysesHint')} icon={ICONS.analysis} accent="green"
          linkText={t('viewAnalyses')} linkTo="/admin/analyses" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiActivities')} value={metrics.recentActivitiesCount} previousValue={previous.recentActivitiesCount}
          hint={t('kpiActivitiesHint', { days: period })} icon={ICONS.activity} accent="purple"
          linkText={t('viewLogs')} linkTo="/admin/activities" trendLabel={t('vsPrevious')} />
        <KpiCard title={t('kpiSubscription')} value={metrics.subscriptionDaysRemaining} previousValue={previous.subscriptionDaysRemaining}
          hint={t('kpiSubscriptionHint')} icon={ICONS.subscription} accent={subUrgent ? 'red' : 'amber'}
          linkText={t('manageSubscription')} linkTo="/admin/subscription" trendLabel={t('vsPrevious')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t('activityTitle')} subtitle={t('activitySubtitle', { days: period })}
          className="lg:col-span-2">
          {activityOption._hasData ? (
            <EChart option={activityOption} height={300} />
          ) : (
            <EmptyState message={t('noActivity')} />
          )}
        </ChartCard>
        <ChartCard title={t('hygieneTitle')} subtitle={t('hygieneSubtitle')}>
          {hygieneOption ? (
            <EChart option={hygieneOption} height={300} />
          ) : (
            <EmptyState message={t('noMfa')} />
          )}
        </ChartCard>
      </div>

      <ChartCard title={t('quotaTitle')} subtitle={t('quotaSubtitle')}
        action={companyInfo?.name ? <span className="text-xs text-slate-500">{companyInfo.name}</span> : null}>
        {quotaData && (quotaData.usersLimit || quotaData.analysesLimit || quotaData.storageLimit) ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quotaData.usersLimit != null && (
              <QuotaBar label={t('quotaUsers')} used={quotaData.usersUsed} limit={quotaData.usersLimit} />
            )}
            {quotaData.analysesLimit != null && (
              <QuotaBar label={t('quotaAnalyses')} used={quotaData.analysesUsed} limit={quotaData.analysesLimit} />
            )}
            {quotaData.storageLimit != null && (
              <QuotaBar label={t('quotaStorage')} used={quotaData.storageUsed} limit={quotaData.storageLimit} />
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <EmptyState message={t('noQuota')} />
            <Link to="/admin/subscription" className="text-sm font-medium text-blue-600 hover:text-blue-800">
              {t('manageSubscription')}
            </Link>
          </div>
        )}
      </ChartCard>
    </div>
  );
};

AdminOverview.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  period: PropTypes.number.isRequired
};

export default AdminOverview;
